import React, { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown, Edit, MoreVertical, Plus, X } from 'lucide-react';
import { TableDataEditor } from '../TableDataEditor';
import { createPortal } from 'react-dom';
import { SelectOptionItem } from './SelectOptionItem';
import { MobileSelectOptionItem } from './MobileSelectOptionItem';
import { createSelectOption, SelectOption } from '../types/select-options';
import { COLOR_MAPPINGS } from '../../../../utils/colorUtils';

// Portal component for dropdowns to avoid z-index issues
function DropdownPortal({
  children,
  targetRef,
}: {
  children: React.ReactNode;
  targetRef: React.RefObject<HTMLElement>;
}) {
  const [mounted, setMounted] = useState(false);
  const portalRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    // Create the portal container
    const portalContainer = document.createElement('div');
    portalContainer.style.position = 'absolute';
    portalContainer.style.zIndex = '9999';
    document.body.appendChild(portalContainer);
    portalRef.current = portalContainer;
    setMounted(true);

    // Position the portal relative to the target element
    const positionPortal = () => {
      if (targetRef.current && portalContainer) {
        const rect = targetRef.current.getBoundingClientRect();
        portalContainer.style.position = 'absolute';
        portalContainer.style.left = `${rect.left}px`;
        portalContainer.style.top = `${rect.bottom + window.scrollY}px`;
        portalContainer.style.width = `${Math.max(rect.width, 200)}px`;
      }
    };

    // Position initially and on resize
    positionPortal();
    window.addEventListener('resize', positionPortal);
    window.addEventListener('scroll', positionPortal);

    // Cleanup
    return () => {
      window.removeEventListener('resize', positionPortal);
      window.removeEventListener('scroll', positionPortal);
      document.body.removeChild(portalContainer);
    };
  }, [targetRef]);

  return mounted && portalRef.current ? createPortal(children, portalRef.current) : null;
}

interface CellProps {
  value: any;
  columnType: string;
  rowId: string;
  columnId: string;
  isEditing: boolean;
  isMobile: boolean;
  formattedValue: string;
  columnOptions: any;
  updateCell: (rowId: string, columnId: string, value: string) => Promise<void>;
  setEditingCell: React.Dispatch<React.SetStateAction<{ rowId: string; columnId: string } | null>>;
  setCellMenuOpen?: React.Dispatch<
    React.SetStateAction<{ rowId: string; columnId: string } | null>
  >;
  cellMenuOpen?: { rowId: string; columnId: string } | null;
  rows: any[];
  addRow: (tableId: string, data: any) => Promise<any>;
  tableDefinition: any;
}

// Cell action menu for mobile
interface CellActionMenuProps {
  isOpen: boolean;
  onClose: () => void;
  onEdit: () => void;
  onClear: () => void;
  hasContent: boolean;
  isBooleanCell: boolean;
  isChecked: boolean;
  onToggle: () => void;
  isSelectCell?: boolean;
  onSelect?: (value: string) => void;
  selectOptions?: string[];
  currentValue?: string;
  // For internal component use only - not passed by parents
  _columnId?: string;
}

export function CellActionMenu({
  isOpen,
  onClose,
  onEdit,
  onClear,
  hasContent,
  isBooleanCell,
  isChecked,
  onToggle,
  isSelectCell = false,
  onSelect,
  selectOptions = [],
  currentValue,
}: CellActionMenuProps) {
  const menuRef = React.useRef<HTMLDivElement>(null);
  const [showSelectOptions, setShowSelectOptions] = useState(false);
  // Position the menu using a calculation based on the cursor position
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });

  // Add event listener for clicks outside the menu (works with both mouse and touch)
  React.useEffect(() => {
    if (!isOpen) return;

    // Add a small delay to prevent the menu from closing immediately
    const handleClickOutsideTimeout = setTimeout(() => {
      function handleClickOutside(event: MouseEvent | TouchEvent) {
        // Make sure we're not closing the menu if the user is interacting with it
        if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
          onClose();
        }
      }

      // Add the event listeners for both mouse and touch events
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);

      // Clean up
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('touchstart', handleClickOutside);
      };
    }, 100); // 100ms delay to let the menu initialize and handle the initial tap

    return () => {
      clearTimeout(handleClickOutsideTimeout);
    };
  }, [isOpen, onClose]);

  // Set initial position when menu opens
  useEffect(() => {
    if (!isOpen) return;

    // Get cursor position from a stored global variable if available
    const lastClickPosition = (window as any)._lastCellMenuClickPosition || {
      x: window.innerWidth / 2,
      y: window.innerHeight / 2,
    };

    // Position the menu below the cursor, but keep it in viewport
    const menuWidth = 220; // Approximate width of menu
    const menuHeight = 250; // Approximate height of menu - increased for safety

    // Position in the center of the screen for better mobile visibility, with an offset
    // from the tap position to prevent immediate re-tap
    let left = lastClickPosition.x - menuWidth / 2;
    let top = lastClickPosition.y + 20; // Position below tap point with padding

    // Make sure menu stays on-screen
    left = Math.max(10, Math.min(left, window.innerWidth - menuWidth - 10));

    // For better mobile experience, try to show the menu higher up on the screen
    // so keyboard won't overlap with it
    top = Math.min(top, window.innerHeight - menuHeight - 50);

    // Ensure a minimum distance from the top
    top = Math.max(50, top);

    setMenuPosition({ top, left });
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      ref={menuRef}
      className="fixed bg-white shadow-lg rounded-lg z-[9999] border border-gray-200 min-w-[200px] max-w-[90vw]"
      style={{ top: `${menuPosition.top}px`, left: `${menuPosition.left}px` }}
    >
      <ul className="py-1">
        {!isBooleanCell && !isSelectCell && (
          <li>
            <button
              className="flex items-center w-full px-4 py-4 text-base text-gray-700 hover:bg-gray-100 active:bg-gray-200"
              onClick={() => {
                onEdit();
                onClose();
              }}
              style={{ touchAction: 'manipulation' }}
            >
              <Edit size={16} className="mr-3" />
              Edit
            </button>
          </li>
        )}

        {isBooleanCell && (
          <li>
            <button
              className="flex items-center w-full px-4 py-4 text-base text-gray-700 hover:bg-gray-100 active:bg-gray-200"
              onClick={() => {
                onToggle();
                onClose();
              }}
              style={{ touchAction: 'manipulation' }}
            >
              <Check size={16} className="mr-3" />
              {isChecked ? 'Uncheck' : 'Check'}
            </button>
          </li>
        )}

        {/* Select option dropdown for mobile */}
        {isSelectCell && (
          <li>
            <div className="w-full">
              {!showSelectOptions ? (
                <button
                  className="flex items-center w-full px-4 py-4 text-base text-gray-700 hover:bg-gray-100 active:bg-gray-200"
                  onClick={() => setShowSelectOptions(true)}
                  style={{ touchAction: 'manipulation' }}
                >
                  <ChevronDown size={16} className="mr-3" />
                  Select an option
                </button>
              ) : (
                <div className="border-t border-gray-100">
                  <div className="max-h-[200px] overflow-y-auto">
                    {selectOptions && selectOptions.length > 0 ? (
                      (selectOptions as SelectOption[]).map((option, index) => (
                        <MobileSelectOptionItem
                          key={`mobile-${option.id}-${index}`}
                          option={option}
                          isSelected={option.id === currentValue}
                          onSelect={() => {
                            if (onSelect) {
                              onSelect(option.id);
                            }
                            onClose();
                          }}
                          onEdit={newLabel => {
                            // Get current options
                            const currentOptions = [...(selectOptions as SelectOption[])];
                            // Update option with same ID but new label
                            const updatedOption = { ...option, label: newLabel };
                            // Replace the option
                            const updatedOptions = currentOptions.map(opt =>
                              opt.id === option.id ? updatedOption : opt
                            );

                            // Dispatch event to update options
                            document.dispatchEvent(
                              new CustomEvent('update-column-options', {
                                detail: {
                                  columnId: _columnId,
                                  options: updatedOptions,
                                },
                              })
                            );

                            // Cell values don't need to be updated since they store IDs
                          }}
                          onColorChange={newColor => {
                            // Get current options
                            const currentOptions = [...(selectOptions as SelectOption[])];
                            // Update option with same ID but new color
                            const updatedOption = { ...option, color: newColor };
                            // Replace the option
                            const updatedOptions = currentOptions.map(opt =>
                              opt.id === option.id ? updatedOption : opt
                            );

                            // Dispatch event to update options
                            document.dispatchEvent(
                              new CustomEvent('update-column-options', {
                                detail: {
                                  columnId: _columnId,
                                  options: updatedOptions,
                                },
                              })
                            );
                          }}
                        />
                      ))
                    ) : (
                      <div className="px-4 py-3 text-sm text-gray-500">
                        Please define the first option
                      </div>
                    )}
                    <div className="border-t border-gray-100">
                      <button
                        className="flex items-center w-full px-4 py-3 text-base text-blue-600 hover:bg-blue-50"
                        onClick={() => {
                          // Switch back to edit view on mobile (which will show the select dropdown editor)
                          onEdit();
                          onClose();
                        }}
                      >
                        <Plus size={16} className="mr-3" />
                        Add option...
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </li>
        )}

        {hasContent && (
          <li>
            <button
              className="flex items-center w-full px-4 py-4 text-base text-gray-700 hover:bg-gray-100 active:bg-gray-200"
              onClick={() => {
                onClear();
                onClose();
              }}
              style={{ touchAction: 'manipulation' }}
            >
              <X size={16} className="mr-3" />
              Clear
            </button>
          </li>
        )}
      </ul>
    </div>
  );
}

export function BooleanCell({
  value,
  rowId,
  columnId,
  updateCell,
  isMobile,
  setCellMenuOpen,
  cellMenuOpen,
}: Pick<
  CellProps,
  'value' | 'rowId' | 'columnId' | 'updateCell' | 'isMobile' | 'setCellMenuOpen' | 'cellMenuOpen'
>) {
  // Handler to prevent clicks inside the cell menu from toggling the value
  const handleClick = (e: React.MouseEvent) => {
    // Don't do anything if the menu is already open and the click is inside a menu button
    if (cellMenuOpen?.rowId === rowId && cellMenuOpen?.columnId === columnId) {
      const target = e.target as HTMLElement;
      if (target.closest('.cell-menu-button')) {
        return;
      }
    }

    if (!isMobile) {
      // Toggle the boolean value directly without opening editor (desktop only)
      const newValue = !value;
      void updateCell(rowId, columnId, String(newValue));
    } else if (setCellMenuOpen) {
      // Open menu on mobile
      setCellMenuOpen({ rowId, columnId });
    }
  };

  return (
    <div
      className="h-full w-full relative cursor-pointer bg-white"
      onClick={handleClick}
      onMouseEnter={
        !isMobile
          ? e => {
              const button = e.currentTarget.querySelector('button.clear-button');
              if (button) button.style.display = 'block';
            }
          : undefined
      }
      onMouseLeave={
        !isMobile
          ? e => {
              const button = e.currentTarget.querySelector('button.clear-button');
              if (button) button.style.display = 'none';
            }
          : undefined
      }
    >
      {/* Fixed layout for the checkbox with no nested positioning - larger for touch */}
      <div className="flex justify-center items-center h-full">
        {value ? (
          <span className="text-green-500 text-xl w-8 h-8 flex items-center justify-center">✓</span>
        ) : (
          <span className="text-gray-200 text-xl w-8 h-8 flex items-center justify-center">◯</span>
        )}
      </div>

      {/* Desktop: Clear button */}
      {!isMobile && value && (
        <button
          className="clear-button absolute right-1 top-1/2 -translate-y-1/2 hidden text-gray-300 hover:text-gray-500 transition-opacity"
          onClick={e => {
            e.stopPropagation(); // Prevent toggling the boolean
            updateCell(rowId, columnId, 'false');
          }}
          title="Clear cell"
        >
          <X size={14} />
        </button>
      )}

      {/* Mobile: Action button with larger touch target */}
      {isMobile && (
        <button
          className="absolute right-0 top-0 h-full w-10 flex items-center justify-center text-gray-400 cell-menu-button"
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();
            // Store click position for menu positioning
            (window as any)._lastCellMenuClickPosition = { x: e.clientX, y: e.clientY };
            // Use a small delay to make sure the click event is properly handled
            if (setCellMenuOpen) {
              setTimeout(() => {
                setCellMenuOpen({ rowId, columnId });
              }, 10);
            }
          }}
          style={{ touchAction: 'manipulation' }}
        >
          <MoreVertical size={18} />
        </button>
      )}

      {/* Mobile: Action menu */}
      {isMobile &&
        setCellMenuOpen &&
        cellMenuOpen?.rowId === rowId &&
        cellMenuOpen?.columnId === columnId &&
        createPortal(
          <CellActionMenu
            isOpen={true}
            onClose={() => setCellMenuOpen(null)}
            onEdit={() => {}}
            onClear={() => {
              updateCell(rowId, columnId, 'false');
              setCellMenuOpen(null);
            }}
            hasContent={value}
            isBooleanCell={true}
            isChecked={value}
            onToggle={() => {
              const newValue = !value;
              updateCell(rowId, columnId, String(newValue));
              setCellMenuOpen(null);
            }}
          />,
          document.body
        )}
    </div>
  );
}

// SelectCell component - handles select type fields
export function SelectCell({
  value,
  rowId,
  columnId,
  isEditing,
  isMobile,
  formattedValue,
  columnOptions,
  updateCell,
  setEditingCell,
  setCellMenuOpen,
  cellMenuOpen,
}: Pick<
  CellProps,
  | 'value'
  | 'rowId'
  | 'columnId'
  | 'isEditing'
  | 'isMobile'
  | 'formattedValue'
  | 'columnOptions'
  | 'updateCell'
  | 'setEditingCell'
  | 'setCellMenuOpen'
  | 'cellMenuOpen'
>) {
  const [showOptions, setShowOptions] = useState(false);
  const [showAddOption, setShowAddOption] = useState(false);
  const [newOption, setNewOption] = useState('');
  const optionsRef = useRef<HTMLDivElement>(null);

  // Get the current options or initialize with empty array
  const options: SelectOption[] =
    columnOptions?.options && Array.isArray(columnOptions.options)
      ? (columnOptions.options as SelectOption[])
      : [];
  const cellRef = useRef<HTMLDivElement>(null);

  // Click outside handler for dropdown
  useEffect(() => {
    if (!showOptions) return;

    const handleClickOutside = (e: MouseEvent) => {
      // Only check if the click is outside both the cell and the dropdown
      const isOutsideCell = cellRef.current && !cellRef.current.contains(e.target as Node);
      const isOutsideOptions = optionsRef.current && !optionsRef.current.contains(e.target as Node);

      if (isOutsideCell && isOutsideOptions) {
        setShowOptions(false);
        setShowAddOption(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showOptions]);

  // Helper to add a new option
  const addNewOption = () => {
    if (!newOption.trim()) return;

    // Create a new option with ID and label
    const newSelectOption = createSelectOption(newOption.trim());

    // Check if this option already exists (by label)
    const existingOption = options.find(
      opt => opt.label.toLowerCase() === newSelectOption.label.toLowerCase()
    );

    if (!existingOption) {
      const updatedOptions = [...options, newSelectOption];

      // Dispatch an event to update the column options
      document.dispatchEvent(
        new CustomEvent('update-column-options', {
          detail: {
            columnId,
            options: updatedOptions,
          },
        })
      );

      // Select the new option for this cell (store the ID)
      updateCell(rowId, columnId, newSelectOption.id);
    } else {
      // If option already exists, just select it
      updateCell(rowId, columnId, existingOption.id);
    }

    // Reset state
    setNewOption('');
    setShowAddOption(false);
    setShowOptions(false);
  };

  // Render the select cell dropdown
  if (isEditing) {
    return (
      <TableDataEditor
        value={value}
        columnType="select"
        columnOptions={columnOptions || {}}
        onChange={newValue => {
          console.log('Cell value changing locally:', { rowId, columnId, value: newValue });
        }}
        onBlur={finalValue => {
          if (finalValue !== undefined) {
            updateCell(rowId, columnId, String(finalValue));
          }
          setEditingCell(null);
        }}
        onKeyDown={(e, currentValue) => {
          if (e.key === 'Enter' && currentValue !== undefined) {
            updateCell(rowId, columnId, String(currentValue));
            setEditingCell(null);
          }
          if (e.key === 'Escape') {
            setEditingCell(null);
          }
        }}
      />
    );
  }

  // Find the selected option
  const selectedOption = value ? options.find(opt => opt.id === value) : null;
  const displayLabel = selectedOption ? selectedOption.label : '';

  return (
    <div className="h-full min-h-[40px] w-full relative" ref={cellRef}>
      <div
        className="p-3 cursor-pointer h-full w-full absolute bg-white hover:bg-gray-50 flex items-center justify-between"
        onClick={() => {
          if (isMobile && setCellMenuOpen) {
            setCellMenuOpen({ rowId, columnId });
          } else {
            setShowOptions(!showOptions);
          }
        }}
        onMouseEnter={
          !isMobile
            ? e => {
                const button = e.currentTarget.querySelector('button.clear-button');
                if (button) button.style.display = 'block';
              }
            : undefined
        }
        onMouseLeave={
          !isMobile
            ? e => {
                const button = e.currentTarget.querySelector('button.clear-button');
                if (button) button.style.display = 'none';
              }
            : undefined
        }
      >
        <div className="truncate pr-5 h-full flex items-center" title={displayLabel}>
          {displayLabel ? (
            <div className="inline-flex items-center gap-2">
              <div
                className={`w-3 h-3 rounded-full ${selectedOption?.color ? COLOR_MAPPINGS.dot[selectedOption.color] : 'bg-gray-400'}`}
              ></div>
              <span className="text-sm">{displayLabel}</span>
            </div>
          ) : (
            <span></span>
          )}
        </div>

        {!isMobile && (
          <div className="flex items-center">
            {/* Clear button on hover */}
            {formattedValue && (
              <button
                className="clear-button text-gray-300 hover:text-gray-500 transition-opacity hidden"
                onClick={e => {
                  e.stopPropagation();
                  updateCell(rowId, columnId, '');
                }}
                title="Clear cell"
              >
                <X size={14} />
              </button>
            )}
          </div>
        )}

        {/* Mobile action button */}
        {isMobile && setCellMenuOpen && (
          <button
            className="absolute right-0 top-0 h-full w-10 flex items-center justify-center text-gray-400 cell-menu-button"
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              // Store click position for menu positioning
              (window as any)._lastCellMenuClickPosition = { x: e.clientX, y: e.clientY };
              // Use a small delay to make sure the click event is properly handled
              setTimeout(() => {
                setCellMenuOpen({ rowId, columnId });
              }, 10);
            }}
            style={{ touchAction: 'manipulation' }}
          >
            <MoreVertical size={18} />
          </button>
        )}
      </div>

      {/* Dropdown menu in portal to avoid z-index issues */}
      {showOptions && !isMobile && (
        <DropdownPortal targetRef={cellRef}>
          <div
            ref={optionsRef}
            className="mt-1 bg-white shadow-lg rounded border border-gray-200 min-w-[200px]"
          >
            <div className="max-h-[250px] overflow-y-auto py-1">
              {/* Select options */}
              {options.length > 0 ? (
                options.map((option, index) => (
                  <SelectOptionItem
                    key={`${option.id}-${index}`}
                    option={option}
                    isSelected={value === option.id}
                    onSelect={() => {
                      updateCell(rowId, columnId, option.id);
                      setShowOptions(false);
                    }}
                    onEdit={newLabel => {
                      // Get current options
                      const currentOptions = [...options];
                      // Create updated option with same ID but new label
                      const updatedOption = { ...option, label: newLabel };
                      // Replace the option
                      const updatedOptions = currentOptions.map(opt =>
                        opt.id === option.id ? updatedOption : opt
                      );

                      // Dispatch event to update options
                      document.dispatchEvent(
                        new CustomEvent('update-column-options', {
                          detail: {
                            columnId,
                            options: updatedOptions,
                          },
                        })
                      );

                      // Cell values using this option don't need to be updated
                      // since they store the ID which remains the same
                    }}
                    onColorChange={newColor => {
                      // Get current options
                      const currentOptions = [...options];
                      // Create updated option with same ID and label but new color
                      const updatedOption = { ...option, color: newColor };
                      // Replace the option
                      const updatedOptions = currentOptions.map(opt =>
                        opt.id === option.id ? updatedOption : opt
                      );

                      // Dispatch event to update options
                      document.dispatchEvent(
                        new CustomEvent('update-column-options', {
                          detail: {
                            columnId,
                            options: updatedOptions,
                          },
                        })
                      );
                    }}
                  />
                ))
              ) : (
                <div className="px-4 py-3 text-sm text-gray-500">
                  Please define the first option
                </div>
              )}

              {/* Add new option UI */}
              {showAddOption ? (
                <div className="px-3 py-3 border-t border-gray-100">
                  <div className="flex flex-col space-y-2">
                    <input
                      type="text"
                      className="w-full text-sm p-2 border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-300"
                      placeholder="Type a new option..."
                      value={newOption}
                      onChange={e => setNewOption(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') addNewOption();
                        if (e.key === 'Escape') {
                          setShowAddOption(false);
                          setNewOption('');
                        }
                      }}
                      autoFocus
                    />
                    <div className="flex justify-end space-x-2">
                      <button
                        className="text-gray-500 text-xs px-2 py-1 hover:text-gray-700"
                        onClick={() => {
                          setShowAddOption(false);
                          setNewOption('');
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        className="bg-blue-50 text-blue-600 text-xs font-medium px-3 py-1 rounded hover:bg-blue-100"
                        onClick={addNewOption}
                      >
                        Add
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <button
                  className="w-full text-left px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 border-t border-gray-100"
                  onClick={e => {
                    e.stopPropagation();
                    setShowAddOption(true);
                  }}
                >
                  <div className="flex items-center">
                    <Plus size={14} className="mr-2" />
                    Add option...
                  </div>
                </button>
              )}
            </div>
          </div>
        </DropdownPortal>
      )}

      {/* Mobile action menu */}
      {isMobile &&
        setCellMenuOpen &&
        cellMenuOpen?.rowId === rowId &&
        cellMenuOpen?.columnId === columnId &&
        createPortal(
          <CellActionMenu
            isOpen={true}
            onClose={() => setCellMenuOpen(null)}
            onEdit={() => setEditingCell({ rowId, columnId })}
            onClear={() => {
              updateCell(rowId, columnId, '');
              setCellMenuOpen(null);
            }}
            hasContent={!!displayLabel}
            isBooleanCell={false}
            isChecked={false}
            onToggle={() => {}}
            isSelectCell={true}
            selectOptions={options}
            currentValue={value}
            _columnId={columnId}
            onSelect={selectedOptionId => {
              updateCell(rowId, columnId, selectedOptionId);
              setCellMenuOpen(null);
            }}
          />,
          document.body
        )}
    </div>
  );
}

export function StandardCell({
  value,
  rowId,
  columnId,
  isEditing,
  isMobile,
  formattedValue,
  columnType,
  columnOptions,
  updateCell,
  setEditingCell,
  setCellMenuOpen,
  cellMenuOpen,
  rows,
  addRow,
  tableDefinition,
}: CellProps) {
  // Special handling for select cells
  if (columnType === 'select') {
    return (
      <SelectCell
        value={value}
        rowId={rowId}
        columnId={columnId}
        isEditing={isEditing}
        isMobile={isMobile}
        formattedValue={formattedValue}
        columnOptions={columnOptions}
        updateCell={updateCell}
        setEditingCell={setEditingCell}
        setCellMenuOpen={setCellMenuOpen}
        cellMenuOpen={cellMenuOpen}
      />
    );
  }
  
  // Special handling for boolean cells
  if (columnType === 'boolean') {
    return (
      <div className="h-full min-h-[40px] w-full relative">
        <div
          className="p-3 cursor-pointer h-full w-full bg-white hover:bg-gray-50 flex items-center"
          onClick={() => {
            // Toggle boolean value
            updateCell(rowId, columnId, (!value).toString());
          }}
        >
          <div className="flex items-center justify-center w-full">
            {value ? (
              <div className="text-green-600 font-medium">✓</div>
            ) : (
              <div className="text-gray-400">-</div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full min-h-[40px] w-full relative">
      {isEditing ? (
        <TableDataEditor
          value={value}
          columnType={columnType}
          columnOptions={columnOptions || {}}
          onChange={newValue => {
            // Don't update on every keystroke - just store locally in the editor
            console.log('Cell value changing locally:', { rowId, columnId, value: newValue });
          }}
          onBlur={finalValue => {
            console.log('Cell editor lost focus with value:', finalValue);
            if (finalValue !== undefined) {
              console.log('BLUR: Saving cell value to DB:', finalValue);
              // Always save the value, regardless of whether it changed
              updateCell(rowId, columnId, String(finalValue));
            }
            setEditingCell(null);
          }}
          onKeyDown={(e, currentValue) => {
            console.log('Key pressed in cell editor:', e.key, 'Current value:', currentValue);
            if (e.key === 'Enter') {
              if (currentValue !== undefined) {
                console.log('ENTER: Saving cell value to DB:', currentValue);
                // Always save the value, regardless of whether it changed
                updateCell(rowId, columnId, String(currentValue));
              }

              // Find the next row in the table
              const currentRowIndex = rows.findIndex(r => r.id === rowId);
              if (currentRowIndex < rows.length - 1) {
                // If not the last row, move to the cell below
                const nextRowId = rows[currentRowIndex + 1].id;
                setEditingCell({ rowId: nextRowId, columnId });
              } else {
                // If it's the last row, add a new row and move to it
                if (tableDefinition?.id) {
                  // Add new row
                  const newRowIndex = rows.length;
                  addRow(tableDefinition.id, { order_index: newRowIndex })
                    .then(newRow => {
                      if (newRow && newRow.id) {
                        // After the row is added, move to the new cell
                        setEditingCell({ rowId: newRow.id, columnId });
                      } else {
                        // If row creation failed, just stop editing
                        setEditingCell(null);
                      }
                    })
                    .catch(err => {
                      console.error('Failed to add new row:', err);
                      setEditingCell(null);
                    });
                } else {
                  // If no table definition, just stop editing
                  setEditingCell(null);
                }
              }
            }
            if (e.key === 'Escape') {
              // Discard changes
              setEditingCell(null);
            }
          }}
        />
      ) : (
        <div
          className="p-3 cursor-pointer h-full w-full absolute bg-white hover:bg-gray-50 relative"
          onClick={e =>
            isMobile && setCellMenuOpen
              ? // Store click position for menu positioning
                (((window as any)._lastCellMenuClickPosition = { x: e.clientX, y: e.clientY }),
                setCellMenuOpen({ rowId, columnId }))
              : setEditingCell({ rowId, columnId })
          }
          onMouseEnter={
            !isMobile
              ? e => {
                  const button = e.currentTarget.querySelector('button.clear-button');
                  if (button) button.style.display = 'block';
                }
              : undefined
          }
          onMouseLeave={
            !isMobile
              ? e => {
                  const button = e.currentTarget.querySelector('button.clear-button');
                  if (button) button.style.display = 'none';
                }
              : undefined
          }
        >
          <div
            className={`truncate pr-5 ${columnType === 'number' ? 'text-center' : ''}`}
            title={formattedValue.length > 20 ? formattedValue : ''}
          >
            {formattedValue}
          </div>

          {/* Desktop: Clear button on hover */}
          {!isMobile && formattedValue && (
            <button
              className="clear-button absolute right-1 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-500 transition-opacity hidden"
              onClick={e => {
                e.stopPropagation(); // Prevent triggering the cell edit
                updateCell(rowId, columnId, '');
              }}
              title="Clear cell"
            >
              <X size={14} />
            </button>
          )}

          {/* Mobile: Action button with larger touch target */}
          {isMobile && setCellMenuOpen && (
            <button
              className="absolute right-0 top-0 h-full w-10 flex items-center justify-center text-gray-400 cell-menu-button"
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                // Store click position for menu positioning
                (window as any)._lastCellMenuClickPosition = { x: e.clientX, y: e.clientY };
                // Use a small delay to make sure the click event is properly handled
                setTimeout(() => {
                  setCellMenuOpen({ rowId, columnId });
                }, 10);
              }}
              style={{ touchAction: 'manipulation' }}
            >
              <MoreVertical size={18} />
            </button>
          )}

          {/* Mobile: Action menu */}
          {isMobile &&
            setCellMenuOpen &&
            cellMenuOpen?.rowId === rowId &&
            cellMenuOpen?.columnId === columnId &&
            createPortal(
              <CellActionMenu
                isOpen={true}
                onClose={() => setCellMenuOpen(null)}
                onEdit={() => setEditingCell({ rowId, columnId })}
                onClear={() => {
                  updateCell(rowId, columnId, '');
                  setCellMenuOpen(null);
                }}
                hasContent={!!formattedValue}
                isBooleanCell={false}
                isChecked={false}
                onToggle={() => {}}
              />,
              document.body
            )}
        </div>
      )}
    </div>
  );
}
